import React, { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { authClient, useSession } from '@/lib/auth-client';
import { useRoleStore } from '@/lib/store';
import { useBranches, useCurrentUser, useUpdateCurrentUser } from '@/hooks/use-api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Camera, CameraIcon, Check, KeyRound, Loader2, Mail, Pencil, RotateCw, Save, UserCircle2, X, ZoomIn } from 'lucide-react';
import { toast } from 'sonner';
import { fileToObjectURL, getInitials, processProfileImage, type CropArea } from './utils';

export function ProfileTab() {
  const role = useRoleStore((s) => s.role);
  const storedBranchId = useRoleStore((s) => s.branchId);
  const { data: session, isPending: sessionLoading } = useSession();
  const { data: currentUser } = useCurrentUser();
  const { data: branches } = useBranches(role !== 'Client');
  const updateCurrentUser = useUpdateCurrentUser();

  const sessionUser = session?.user as { id: string; name?: string; email?: string; role?: string; branchId?: string; image?: string } | undefined;

  const profileUser = currentUser || {
    id: sessionUser?.id || '',
    name: sessionUser?.name || 'Authenticated User',
    email: sessionUser?.email || '',
    role: sessionUser?.role || role,
    branchId: sessionUser?.branchId || storedBranchId || 'Unassigned',
    territory: '',
    avatar: sessionUser?.image || '',
    image: sessionUser?.image || '',
  };

  const [profileForm, setProfileForm] = useState({ name: '', email: '', territory: '', image: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Image editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedArea, setCroppedArea] = useState<CropArea | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    setProfileForm({
      name: profileUser.name || '',
      email: profileUser.email || '',
      territory: profileUser.territory || '',
      image: profileUser.image || profileUser.avatar || '',
    });
  }, [profileUser.avatar, profileUser.email, profileUser.image, profileUser.name, profileUser.territory]);

  // Cleanup object URL on close
  useEffect(() => {
    return () => { if (imageSrc.startsWith('blob:')) URL.revokeObjectURL(imageSrc); };
  }, [imageSrc]);

  const displayName = profileUser.name || 'Authenticated User';
  const displayEmail = profileUser.email || 'No email available';
  const displayRole = profileUser.role || role;
  const displayBranchId = profileUser.branchId || storedBranchId || 'Unassigned';
  const displayBranchName = branches?.find((b) => b.id === displayBranchId)?.name || displayBranchId;
  const avatarSrc = profileForm.image || profileUser.image || profileUser.avatar || '';
  const initials = getInitials(displayName);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedAreaPixels);
  }, []);

  const openEditor = (file: File) => {
    if (imageSrc.startsWith('blob:')) URL.revokeObjectURL(imageSrc);
    const url = fileToObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedArea(null);
    setEditorOpen(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    openEditor(file);
    event.target.value = '';
  };

  const handleApply = async () => {
    if (!croppedArea || !imageSrc) return;
    setIsApplying(true);
    try {
      const result = await processProfileImage(imageSrc, croppedArea, rotation);
      setProfileForm((c) => ({ ...c, image: result.base64 }));
      toast.success(`Photo ready (${result.sizeKB}KB) — save profile to persist.`);
      setEditorOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to process image');
    } finally {
      setIsApplying(false);
    }
  };

  const handleEditorCancel = () => {
    setEditorOpen(false);
    if (imageSrc.startsWith('blob:')) URL.revokeObjectURL(imageSrc);
    setImageSrc('');
  };

  const handleSaveProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!profileForm.name || !profileForm.email) { toast.error('Name and email are required'); return; }
    try {
      await updateCurrentUser.mutateAsync({ name: profileForm.name, email: profileForm.email, territory: profileForm.territory, image: profileForm.image });
      toast.success('Profile updated successfully');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update your profile');
    }
  };

  const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) { toast.error('Complete all password fields'); return; }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (passwordForm.newPassword.length < 8) { toast.error('Minimum 8 characters'); return; }
    setIsChangingPassword(true);
    try {
      const result = await authClient.changePassword({ currentPassword: passwordForm.currentPassword, newPassword: passwordForm.newPassword });
      if ((result as { error?: { message?: string } })?.error) throw new Error((result as { error?: { message?: string } }).error?.message || 'Failed');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password updated');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
      {/* Profile Overview */}
      <Card className="overflow-hidden rounded-3xl border border-border bg-card shadow-glass">
        <CardHeader className="border-b bg-muted/30 p-8">
          <CardTitle className="text-foreground font-black uppercase tracking-tight">Profile Overview</CardTitle>
          <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Session-backed identity with editable avatar stored as base64 in D1.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 p-6 sm:p-8">
          <div className="rounded-[2rem] border border-border bg-muted/50 p-6">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
              <div className="relative w-fit">
                <Avatar className="h-24 w-24 border-4 border-white shadow-xl">
                  <AvatarImage src={avatarSrc || undefined} alt={displayName} />
                  <AvatarFallback className="bg-primary text-2xl font-black text-primary-foreground">{initials}</AvatarFallback>
                </Avatar>
                <label className="absolute -bottom-1 -right-1 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-primary text-white shadow-lg transition hover:bg-primary/90">
                  <Camera className="h-4 w-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
                </label>
              </div>
              <div className="space-y-2">
                <p className="text-2xl font-black uppercase tracking-tight text-foreground">{displayName}</p>
                <p className="break-all text-sm font-bold text-muted-foreground">{displayEmail}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge className="border-none bg-secondary text-[10px] font-black uppercase tracking-widest">{displayRole}</Badge>
                  <Badge className="border-none bg-card text-[10px] font-black uppercase tracking-widest">{displayBranchName}</Badge>
                </div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {sessionLoading ? 'Loading session...' : 'Click the camera icon or use the buttons below to change your photo.'}
                </p>
              </div>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-[1.5rem] border border-border bg-card p-5">
              <p className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-muted-foreground"><UserCircle2 className="h-4 w-4" />Full Name</p>
              <p className="text-lg font-black tracking-tight text-foreground">{displayName}</p>
            </div>
            <div className="rounded-[1.5rem] border border-border bg-card p-5">
              <p className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-muted-foreground"><Mail className="h-4 w-4" />Email</p>
              <p className="break-all text-lg font-black tracking-tight text-foreground">{displayEmail}</p>
            </div>
            <div className="rounded-[1.5rem] border border-border bg-card p-5">
              <p className="mb-3 text-xs font-black uppercase tracking-widest text-muted-foreground">Role</p>
              <p className="text-lg font-black tracking-tight text-foreground">{displayRole}</p>
            </div>
            <div className="rounded-[1.5rem] border border-border bg-card p-5">
              <p className="mb-3 text-xs font-black uppercase tracking-widest text-muted-foreground">Territory</p>
              <p className="text-lg font-black tracking-tight text-foreground">{profileUser.territory || 'Not Assigned'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Forms */}
      <div className="space-y-6">
        <Card className="overflow-hidden rounded-3xl border border-border bg-card shadow-glass">
          <CardHeader className="border-b bg-muted/30 p-8">
            <CardTitle className="flex items-center gap-3 text-foreground font-black uppercase tracking-tight"><Pencil className="h-5 w-5" />Edit Profile</CardTitle>
            <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Update name, email, territory, and photo.</CardDescription>
          </CardHeader>
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSaveProfile} className="space-y-5">
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Full Name</Label>
                  <Input value={profileForm.name} onChange={(e) => setProfileForm((c) => ({ ...c, name: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
                </div>
                <div className="space-y-2">
                  <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Email</Label>
                  <Input type="email" value={profileForm.email} onChange={(e) => setProfileForm((c) => ({ ...c, email: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
                </div>
                <div className="space-y-2">
                  <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Territory</Label>
                  <Input value={profileForm.territory} onChange={(e) => setProfileForm((c) => ({ ...c, territory: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" placeholder="Assigned territory" />
                </div>
                <div className="space-y-2">
                  <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Branch</Label>
                  <Input value={displayBranchName} disabled className="h-12 rounded-xl border-border bg-muted/50 font-bold" />
                </div>
              </div>

              {/* Photo upload buttons */}
              <div className="flex flex-wrap gap-3 rounded-[1.5rem] border border-border bg-muted/40 p-4">
                <label className="flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-border bg-card px-4 text-[10px] font-black uppercase text-muted-foreground transition hover:bg-muted/50">
                  <Camera className="h-4 w-4" />Upload Photo
                  <input type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
                </label>
                <label className="flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-border bg-card px-4 text-[10px] font-black uppercase text-muted-foreground transition hover:bg-muted/50">
                  <CameraIcon className="h-4 w-4" />Take Photo
                  <input type="file" accept="image/*" capture="user" className="hidden" onChange={handleFileSelect} />
                </label>
                <p className="flex items-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Auto-cropped to 256×256 JPEG
                </p>
              </div>

              <Button type="submit" disabled={updateCurrentUser.isPending} className="h-12 w-full rounded-xl bg-primary font-black uppercase tracking-tight shadow-lg shadow-primary/20 hover:bg-primary/90">
                {updateCurrentUser.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Save className="mr-2 h-4 w-4" />Save Profile Changes
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="overflow-hidden rounded-3xl border border-border bg-card shadow-glass">
          <CardHeader className="border-b bg-muted/30 p-8">
            <CardTitle className="flex items-center gap-3 text-foreground font-black uppercase tracking-tight"><KeyRound className="h-5 w-5" />Password</CardTitle>
            <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Change your login password.</CardDescription>
          </CardHeader>
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleChangePassword} className="space-y-5">
              <div className="space-y-2">
                <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Current Password</Label>
                <Input type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm((c) => ({ ...c, currentPassword: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" autoComplete="current-password" />
              </div>
              <div className="space-y-2">
                <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">New Password</Label>
                <Input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm((c) => ({ ...c, newPassword: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" autoComplete="new-password" />
              </div>
              <div className="space-y-2">
                <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Confirm New Password</Label>
                <Input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm((c) => ({ ...c, confirmPassword: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" autoComplete="new-password" />
              </div>
              <Button type="submit" disabled={isChangingPassword} className="h-12 w-full rounded-xl bg-primary/90 font-black uppercase tracking-tight text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary">
                {isChangingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Update Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Image Crop/Edit Dialog */}
      <Dialog open={editorOpen} onOpenChange={(open) => { if (!open) handleEditorCancel(); }}>
        <DialogContent className="rounded-[2rem] border-border p-6 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black uppercase tracking-tight text-foreground">Edit Profile Photo</DialogTitle>
            <DialogDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Drag to reposition, pinch/scroll to zoom, then apply.
            </DialogDescription>
          </DialogHeader>

          {/* Cropper area */}
          <div className="relative mx-auto h-64 w-full overflow-hidden rounded-2xl border-2 border-border bg-black sm:h-72">
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
              />
            )}
          </div>

          {/* Controls */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-3">
              <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Slider value={[zoom]} onValueChange={([v]) => setZoom(v)} min={1} max={3} step={0.05} className="flex-1" />
              <span className="w-10 text-right text-xs font-bold text-muted-foreground">{zoom.toFixed(1)}×</span>
            </div>
            <div className="flex items-center gap-3">
              <RotateCw className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Slider value={[rotation]} onValueChange={([v]) => setRotation(v)} min={0} max={360} step={1} className="flex-1" />
              <span className="w-10 text-right text-xs font-bold text-muted-foreground">{rotation}°</span>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2 sm:gap-0">
            <Button variant="ghost" onClick={handleEditorCancel} className="h-11 rounded-xl px-5 text-xs font-black uppercase text-muted-foreground">
              <X className="mr-2 h-4 w-4" />Cancel
            </Button>
            <Button onClick={handleApply} disabled={isApplying || !croppedArea} className="h-11 rounded-xl bg-primary px-5 font-black uppercase tracking-tight shadow-lg shadow-primary/20 hover:bg-primary/90">
              {isApplying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
